/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Upload Document Modal Wrapper (Task 10.3.5)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useEffect } from 'react';
import { useKnowledgeStore } from '../../stores/useKnowledgeStore';
import { useI18n } from '../../hooks/useI18n';
import { DocumentUploadZone } from './DocumentUploadZone';
import { X, UploadCloud } from 'lucide-react';

export const UploadDocumentModal: React.FC = () => {
  const { t } = useI18n();
  const { isUploadModalOpen, closeUploadModal } = useKnowledgeStore();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isUploadModalOpen) {
        closeUploadModal();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isUploadModalOpen, closeUploadModal]);

  if (!isUploadModalOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-in fade-in duration-200">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
        onClick={closeUploadModal}
      />

      {/* Modal Dialog */}
      <div className="relative z-10 w-full max-w-2xl rounded-2xl border border-surface-border bg-surface p-6 shadow-2xl space-y-5 animate-in zoom-in-95 duration-200 max-h-[90vh] overflow-y-auto scrollbar-thin">
        {/* Header */}
        <div className="flex items-center justify-between pb-3 border-b border-surface-border">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
              <UploadCloud className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-heading font-bold text-sm text-foreground">
                {t('knowledge.uploadModalTitle')}
              </h3>
              <p className="text-[11px] text-foreground-muted">
                {t('knowledge.uploadModalSubtitle')}
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={closeUploadModal}
            className="p-1.5 rounded-xl text-foreground-muted hover:text-foreground hover:bg-muted transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Upload Form Body */}
        <DocumentUploadZone onCompleted={closeUploadModal} />
      </div>
    </div>
  );
};
